import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Portal,
  TextField,
  Typography,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  AddressSuggestion,
  StoreLocationView,
  VEND_ORGS,
  getAddressDetails,
  getStoreLocation,
  saveStoreLocation,
  searchAddresses,
} from './storeLocationApi';

export interface StoreLocationFormProps {
  storeId: string;
  onSaved?: (view: StoreLocationView) => void;
  onLoaded?: (view: StoreLocationView) => void;
  submitLabel?: string;
  showMakeLive?: boolean;
  /** Render the save button here instead (e.g. a dialog's fixed footer, so it never needs scrolling to). */
  actionsContainer?: HTMLElement | null;
}

const PIN_COLOR = '#06C167';
const SA_CENTRE: [number, number] = [-29, 24];
const SEARCH_FAILED = "Address search isn't working right now — click the map to place the pin.";

/** A Google Places session token: one per search, from first keystroke to the pick. */
const newSessionToken = () =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const pinIcon = L.divIcon({
  className: 'store-location-pin',
  html: `<div style="width:20px;height:20px;border-radius:50%;background:${PIN_COLOR};border:3px solid #fff;box-shadow:0 0 0 2px ${PIN_COLOR}66,0 2px 6px rgba(0,0,0,.45);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const Recenter: React.FC<{ position: [number, number] | null }> = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position && typeof map?.setView === 'function') map.setView(position, Math.max(map.getZoom(), 16));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.[0], position?.[1]]);
  return null;
};

const ClickToPlace: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click(e: L.LeafletMouseEvent) {
      onPick(Number(e.latlng.lat.toFixed(7)), Number(e.latlng.lng.toFixed(7)));
    },
  });
  return null;
};

const StoreLocationForm: React.FC<StoreLocationFormProps> = ({
  storeId,
  onSaved,
  onLoaded,
  submitLabel = 'Save',
  showMakeLive = false,
  actionsContainer,
}) => {
  const [view, setView] = useState<StoreLocationView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [buildingName, setBuildingName] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [suburb, setSuburb] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [vendOutletId, setVendOutletId] = useState('');
  const [loopBranchId, setLoopBranchId] = useState('');
  const [vendOrg, setVendOrg] = useState<string>('');
  const [makeLive, setMakeLive] = useState(true);

  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const sessionToken = useRef(newSessionToken());
  // Only the latest pick may fill the form: an earlier pick's details can arrive later.
  const pickSeq = useRef(0);
  // Read at search time (not an effect dependency), so moving the pin does not re-run the search.
  const nearRef = useRef<{ lat: number; lng: number } | null>(null);
  nearRef.current = lat != null && lng != null ? { lat, lng } : null;

  const apply = (v: StoreLocationView) => {
    setView(v);
    setBuildingName(v.location.buildingName || '');
    setStreetAddress(v.location.streetAddress || '');
    setSuburb(v.location.suburb || '');
    setCity(v.location.city || '');
    setProvince(v.location.province || '');
    setPostalCode(v.location.postalCode || '');
    setLat(v.location.lat);
    setLng(v.location.lng);
    setVendOutletId(v.routing.vendOutletId || '');
    setLoopBranchId(v.routing.loopBranchId || '');
    const org = (v.routing.vendOrg || '').trim().toLowerCase();
    // Unknown / missing: leave empty so the admin must choose — never silently default.
    setVendOrg((VEND_ORGS as readonly string[]).includes(org) ? org : '');
  };

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setLoadError(null);
    getStoreLocation(storeId)
      .then((v) => {
        if (cancelled) return;
        apply(v);
        onLoaded?.(v);
      })
      .catch((e: Error) => !cancelled && setLoadError(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // Address search: 300 ms debounce, at least 3 characters.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setOptions([]);
      setSearchFailed(false);
      // A cleared search is an abandoned one: the next one is a new billing session.
      if (q.length === 0) sessionToken.current = newSessionToken();
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setSearching(true);
      setSearchFailed(false);
      searchAddresses(q, { sessionToken: sessionToken.current, near: nearRef.current })
        .then((r) => !cancelled && setOptions(Array.isArray(r) ? r : []))
        .catch(() => {
          if (cancelled) return;
          setOptions([]);
          setSearchFailed(true);
        })
        .finally(() => !cancelled && setSearching(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const fill = (s: AddressSuggestion) => {
    setBuildingName(s.buildingName || '');
    setStreetAddress(s.streetAddress || '');
    setSuburb(s.suburb || '');
    setCity(s.city || '');
    setProvince(s.province || '');
    setPostalCode(s.postalCode || '');
    setLat(s.lat);
    setLng(s.lng);
  };

  const pick = async (s: AddressSuggestion | null) => {
    if (!s) return;
    setPickError(null);
    const seq = ++pickSeq.current;
    const token = sessionToken.current;
    // The pick closes this search's billing session; the next search starts a new one.
    sessionToken.current = newSessionToken();
    if (!s.placeId || (s.lat != null && s.lng != null)) {
      setResolving(false);
      fill(s);
      return;
    }
    setResolving(true);
    try {
      const details = await getAddressDetails(s.placeId, token);
      if (seq === pickSeq.current) fill(details);
    } catch (e) {
      if (seq === pickSeq.current) setPickError((e as Error).message);
    } finally {
      if (seq === pickSeq.current) setResolving(false);
    }
  };

  const position: [number, number] | null = lat != null && lng != null ? [lat, lng] : null;
  const markerHandlers = useMemo(
    () => ({
      dragend(e: L.LeafletEvent) {
        const p = (e.target as L.Marker).getLatLng();
        setLat(Number(p.lat.toFixed(7)));
        setLng(Number(p.lng.toFixed(7)));
      },
    }),
    [],
  );

  const canSave = streetAddress.trim().length > 0 && lat != null && lng != null && vendOrg !== '' && !saving && !resolving;

  const save = async () => {
    if (lat == null || lng == null || vendOrg === '') return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await saveStoreLocation(storeId, {
        buildingName,
        streetAddress: streetAddress.trim(),
        suburb,
        city,
        province,
        postalCode,
        lat,
        lng,
        vendOutletId,
        loopBranchId,
        vendOrg: vendOrg as (typeof VEND_ORGS)[number],
        ...(showMakeLive && view && !view.isActive ? { makeLive } : {}),
      });
      apply(saved);
      onLoaded?.(saved);
      onSaved?.(saved);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // With the button in a fixed footer the error sits out of sight: bring it into view.
  const saveErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (saveError) saveErrorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [saveError]);

  const submitButton = (
    <Button variant="contained" onClick={save} disabled={!canSave}>
      {saving ? 'Saving...' : submitLabel}
    </Button>
  );

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!view) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete<AddressSuggestion>
        options={options}
        loading={searching}
        filterOptions={(x) => x}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.label === b.label}
        value={null}
        inputValue={query}
        onInputChange={(_e, v, reason) => reason !== 'reset' && setQuery(v)}
        onChange={(_e, v) => pick(v)}
        noOptionsText={query.trim().length < 3 ? 'Type at least 3 letters' : searchFailed ? SEARCH_FAILED : 'No addresses found'}
        renderInput={(params) => <TextField {...params} label="Search address" size="small" />}
      />
      {resolving && <Typography variant="caption" color="text.secondary">Loading address…</Typography>}
      {pickError && <Alert severity="warning">{pickError}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField label="Building / centre" size="small" value={buildingName} onChange={(e) => setBuildingName(e.target.value)} />
        <TextField label="Street address" size="small" required value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} />
        <TextField label="Suburb" size="small" value={suburb} onChange={(e) => setSuburb(e.target.value)} />
        <TextField label="City" size="small" value={city} onChange={(e) => setCity(e.target.value)} />
        <TextField label="Province" size="small" value={province} onChange={(e) => setProvince(e.target.value)} />
        <TextField label="Postal code" size="small" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Search for an address or click the map to place the pin, then drag it to the exact pickup spot.
        </Typography>
        <Box sx={{ height: 260, borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer center={position ?? SA_CENTRE} zoom={position ? 16 : 5} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles &copy; Esri"
              maxNativeZoom={18}
            />
            {position && <Marker position={position} icon={pinIcon} draggable eventHandlers={markerHandlers} />}
            <ClickToPlace
              onPick={(la, ln) => {
                setLat(la);
                setLng(ln);
              }}
            />
            <Recenter position={position} />
          </MapContainer>
        </Box>
        <Typography variant="caption" color="text.secondary" data-testid="latlng">
          {position ? `Latitude ${lat}, longitude ${lng}` : 'No pin yet. Search for an address or click the map to place one.'}
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Routing (from Radar)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <TextField label="Lightspeed outlet id" size="small" value={vendOutletId} onChange={(e) => setVendOutletId(e.target.value)} />
          <TextField label="Loop branch id" size="small" value={loopBranchId} onChange={(e) => setLoopBranchId(e.target.value)} />
          <TextField
            select
            required
            label="Lightspeed account"
            size="small"
            value={vendOrg}
            onChange={(e) => setVendOrg(e.target.value)}
            helperText={vendOrg === '' ? 'Choose the account this store belongs to' : undefined}
          >
            {VEND_ORGS.map((o) => (
              <MenuItem key={o} value={o}>
                {o.charAt(0).toUpperCase() + o.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {showMakeLive && !view.isActive && (
        <FormControlLabel
          control={<Checkbox checked={makeLive} onChange={(e) => setMakeLive(e.target.checked)} />}
          label="Make the store live after saving"
        />
      )}

      {saveError && <Alert severity="error" ref={saveErrorRef}>{saveError}</Alert>}

      {actionsContainer ? (
        <Portal container={actionsContainer}>{submitButton}</Portal>
      ) : (
        <Box>{submitButton}</Box>
      )}
    </Box>
  );
};

export default StoreLocationForm;
